<template>
  <section>
    <form method="post" @submit.prevent="send" novalidate>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ $t('attributes.update') + $t('attributes.city') }}
          </p>
        </header>
        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group horizontal name="code" :label="$t('attributes.code')">
              <b-input
                v-model="form.code"
                name="code"
                disabled="true"
                :placeholder="
                  $t('message.please_input') + $t('attributes.code')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group horizontal name="name" :label="$t('attributes.name')">
              <b-input
                v-model="form.name"
                name="name"
                :placeholder="
                  $t('message.please_input') + $t('attributes.name')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="english_name"
              :label="$t('attributes.english_name')"
            >
              <b-input
                v-model="form.english_name"
                name="english_name"
                :placeholder="
                  $t('message.please_input') + $t('attributes.english_name')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="area_code"
              attribute="attributes.area"
              :label="$t('attributes.area')"
            >
              <b-select
                v-model="selectedArea"
                name="area_code"
                :loading="isLoadingArea"
                :placeholder="
                  $t('message.please_select') + $t('attributes.area')
                "
                expanded
                @input="onSelectArea"
              >
                <option value="" disabled>
                  {{ $t('message.please_select') + $t('attributes.area') }}
                </option>
                <option
                  v-for="option in areas"
                  :key="option.code"
                  :value="option"
                >
                  {{ option.name }}
                </option>
              </b-select>
            </form-group>

            <form-group
              horizontal
              name="country_id"
              attribute="attributes.country"
              :label="$t('attributes.country')"
            >
              <b-select
                v-model="selectedCountry"
                name="country_id"
                :loading="isLoadingCountry"
                :placeholder="
                  $t('message.please_select') + $t('attributes.country')
                "
                expanded
                @input="onSelectCountry"
              >
                <option value="" disabled>
                  {{ $t('message.please_select') + $t('attributes.country') }}
                </option>
                <option
                  v-for="option in countries"
                  :key="option.id"
                  :value="option"
                >
                  {{ option.name }}
                </option>
              </b-select>
            </form-group>

            <form-group
              horizontal
              name="region_id"
              attribute="attributes.region"
              :label="$t('attributes.region')"
            >
              <b-select
                v-model="selectedRegion"
                name="region_id"
                :loading="isLoadingRegion"
                :placeholder="
                  $t('message.please_select') + $t('attributes.region')
                "
                expanded
                @input="onSelectRegion"
              >
                <option value="" disabled>
                  {{ $t('message.please_select') + $t('attributes.region') }}
                </option>
                <option
                  v-for="option in regions"
                  :key="option.id"
                  :value="option"
                >
                  {{ option.name }}
                </option>
              </b-select>
            </form-group>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button :loading="isSending" @click="$parent.close()">
            {{ $t('attributes.cancel') }}
          </b-button>
          <b-button
            :loading="isSending || isLoading"
            type="is-primary"
            native-type="submit"
          >
            {{ $t('attributes.update') }}
          </b-button>
        </footer>

        <b-loading :is-full-page="false" :active.sync="isLoading"></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { AREA_API, COUNTRIES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'CityUpdateForm',

  props: {
    isSending: {
      type: Boolean,
      default: false
    },
    city: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isLoadingArea: false,
      areas: [],
      selectedArea: null,

      isLoadingCountry: false,
      countries: [],
      selectedCountry: null,

      isLoadingRegion: false,
      regions: [],
      selectedRegion: null,

      form: {
        id: this.city.id,
        area_code: this.city.area_code && this.city.area_code.trim(),
        country_id: this.city.country_id,
        region_id: this.city.region_id,
        code: this.city.code && this.city.code.trim(),
        name: this.city.name && this.city.name.trim(),
        english_name: this.city.english_name && this.city.english_name.trim()
      }
    }
  },
  computed: {
    isLoading() {
      return this.isLoadingArea || this.isLoadingCountry || this.isLoadingRegion
    }
  },
  validations: {
    form: {
      area_code: { required },
      country_id: { required },
      region_id: { required },
      code: { required },
      name: { required },
      english_name: { required }
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        this.isLoadingArea = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)

        if (this.form.area_code) {
          this.selectedArea = this.areas.find(
            a => a.code === this.form.area_code
          )
          this.form.area_code = this.selectedArea.code
        }

        if (this.selectedArea) {
          await this.loadCountries()
        }
      } catch (e) {
        this.areas = []
        this.selectedArea = null

        notifyService.fetch_error('area', e)
      } finally {
        this.isLoadingArea = false
      }
    },
    async loadCountries() {
      try {
        const api = `${AREA_API}/${this.form.area_code}/countries`

        this.isLoadingCountry = true
        const res = await this.axios.get(api)
        this.countries = res.data.data

        if (this.form.country_id) {
          this.selectedCountry = this.countries.find(
            a => a.id === this.form.country_id
          )
          this.form.country_id = this.selectedCountry && this.selectedCountry.id
        }

        if (this.selectedCountry) {
          await this.loadRegions()
        }
      } catch (e) {
        this.countries = []
        this.selectedCountry = null

        notifyService.fetch_error('country', e)
      } finally {
        this.isLoadingCountry = false
      }
    },
    async loadRegions() {
      try {
        const api = `${COUNTRIES_API}/${this.form.country_id}/regions`

        this.isLoadingRegion = true
        const res = await this.axios.get(api)
        this.regions = res.data.data

        if (this.form.region_id) {
          this.selectedRegion = this.regions.find(
            a => a.id === this.form.region_id
          )
          this.form.region_id = this.selectedRegion && this.selectedRegion.id
        }
      } catch (e) {
        this.regions = []
        this.selectedRegion = null

        notifyService.fetch_error('region', e)
      } finally {
        this.isLoadingRegion = false
      }
    },

    onSelectArea(option) {
      this.form.area_code = option && option.code

      this.selectedCountry = null
      this.form.country_id = null

      this.selectedRegion = null
      this.form.region_id = null

      this.loadCountries()
    },
    onSelectCountry(option) {
      this.form.country_id = option && option.id

      this.selectedRegion = null
      this.form.region_id = null

      this.loadRegions()
    },
    onSelectRegion(option) {
      this.form.region_id = option && option.id
    },

    send() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
